import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, Tab, Tabs, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { selectWaActive, selectWaMappedCount, WaRootState } from './selectors';

// SmartSend part B — the two-tab strip. Rendered ONLY while the add-on is active; with the
// feature off the mapping screen has no tabs at all and is pixel-identical to today (14-W3 §2).
//
// Sticky-top, the house idiom from `ChatTemplateModal.tsx:832-856`. It matters here because the
// WhatsApp tab is the second screenful on a 1366×768 laptop: without it the operator scrolls
// down to the phone preview and loses the way back to the e-mail tab.

export type WaTabKey = 'email' | 'whatsapp';

const useStyles = makeStyles((theme) => ({
    strip: {
        position: 'sticky', top: 0, zIndex: 10,
        background: 'var(--pls-d-surface, #fff)', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)',
        marginBottom: theme.spacing(2),
    },
    label: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
    remove: {
        fontSize: 12, fontWeight: 400, textTransform: 'none', color: 'var(--pls-d-text-2, #8a8a8a)',
        textDecoration: 'underline', cursor: 'pointer', borderRadius: 4, padding: '0 2px',
        '&:hover': { color: 'var(--pls-d-danger, #c0392b)' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.light}` },
    },
    removeDisabled: {
        cursor: 'default', opacity: 0.5, textDecoration: 'none',
        '&:hover': { color: 'var(--pls-d-text-2, #8a8a8a)' },
    },
}));

// 09-channel-units — "הסר" beside a tab takes that channel's UNIT out of this send. It replaces the
// channel tiles the page used to repeat from the picker. The owner of the page decides what removing
// means (the e-mail: continue on the WhatsApp unit's own page; the WhatsApp: confirm, then delete
// its unit); this strip only offers the click. Absent handler ⇒ no "הסר" for that channel.
export interface TabRemoveAction {
    onRemove: () => void;
    disabled?: boolean;
    /** Why it is disabled — shown on hover. */
    reason?: string | null;
}

interface Props {
    value: WaTabKey;
    onChange: (tab: WaTabKey) => void;
    /** How many e-mail tokens are mapped, for the first badge. */
    emailMapped: number;
    emailTotal: number;
    removeEmail?: TabRemoveAction | null;
    removeWhatsApp?: TabRemoveAction | null;
}

const ChannelTabs: React.FC<Props> = ({ value, onChange, emailMapped, emailTotal, removeEmail = null, removeWhatsApp = null }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const active = useSelector((s: WaRootState) => selectWaActive(s));
    const waMapped = useSelector((s: WaRootState) => selectWaMappedCount(s));
    const waTotal = useSelector((s: any) => s.smartSendWa.waPlaceholderCount ?? 0);
    // QA BUG-06 — the tooltip of a refused "הסר" portals to document.body, outside App's <div dir>.
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    if (!active) return null;

    const badge = (mapped: number, total: number) => (
        <Chip size="small" label={t('DataSources.send.whatsapp.tabs.badge', {
            mapped: mapped.toLocaleString(), total: total.toLocaleString(),
        })} />
    );

    // A SPAN with the button role, inside a tab whose root is a DIV (`component="div"` below): a
    // focusable control inside a native <button> is invalid HTML. While it is live, every event that
    // would reach the tab stops at the span — the click and Enter/Space (removing a channel must not
    // switch to its tab), mousedown and touchstart (the tab's ripple), focus and keyup (the tab's
    // focus-visible pulse). While it is DISABLED it swallows nothing: a click on it is a click on the
    // tab, and its tooltip says why it is refused.
    const removeLink = (action: TabRemoveAction | null, aria: string) => {
        if (!action) return null;
        const disabled = !!action.disabled;
        const stop = disabled ? undefined : (e: React.SyntheticEvent) => e.stopPropagation();
        const link = (
            <span
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                aria-label={aria}
                className={clsx(classes.remove, disabled && classes.removeDisabled)}
                onMouseDown={stop}
                onTouchStart={stop}
                onFocus={stop}
                onKeyUp={stop}
                onClick={disabled ? undefined : (e) => { e.stopPropagation(); action.onRemove(); }}
                onKeyDown={disabled ? undefined : (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    action.onRemove();
                }}
            >
                {t('DataSources.send.whatsapp.tabs.remove')}
            </span>
        );
        // QA BUG-06 (C-06) — the refusal reason as the page's tooltip (it was a native `title`). The span
        // is not `disabled`, so it takes the Tooltip directly; a live "הסר", or one without a reason,
        // renders bare.
        return disabled && action.reason
            ? <Tooltip title={action.reason} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>{link}</Tooltip>
            : link;
    };
    const emailBadge = t('DataSources.send.whatsapp.tabs.badge', {
        mapped: emailMapped.toLocaleString(), total: emailTotal.toLocaleString(),
    });
    const waBadge = t('DataSources.send.whatsapp.tabs.badge', {
        mapped: waMapped.toLocaleString(), total: waTotal.toLocaleString(),
    });

    return (
        <Box className={classes.strip}>
            <Tabs
                value={value}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_e, v) => onChange(v as WaTabKey)}
                aria-label={t('DataSources.send.channel.selectAria')}
            >
                {/* `component="div"`: the tab hosts the "הסר" control, and a focusable control
                    inside a native <button> is invalid HTML. `aria-label` keeps the tab's name to
                    the channel and its count — without it the name would absorb "הסר". */}
                <Tab
                    value="email"
                    component="div"
                    aria-label={`${t('DataSources.send.whatsapp.tabs.email')} ${emailBadge}`}
                    label={(
                        <span className={classes.label}>
                            {t('DataSources.send.whatsapp.tabs.email')}
                            {badge(emailMapped, emailTotal)}
                            {removeLink(removeEmail, t('DataSources.send.whatsapp.tabs.removeEmailAria'))}
                        </span>
                    )}
                />
                <Tab
                    value="whatsapp"
                    component="div"
                    aria-label={`${t('DataSources.send.whatsapp.tabs.whatsapp')} ${waBadge}`}
                    label={(
                        <span className={classes.label}>
                            {t('DataSources.send.whatsapp.tabs.whatsapp')}
                            {badge(waMapped, waTotal)}
                            {removeLink(removeWhatsApp, t('DataSources.send.whatsapp.tabs.removeWaAria'))}
                        </span>
                    )}
                />
            </Tabs>
        </Box>
    );
};

export default ChannelTabs;
